/**
 * Pure helpers — turn availability rules + windows + blackouts into a flat
 * list of bookable slots over a date range. All times in UTC; the UI renders
 * in the viewer's local timezone.
 */
import type {
  MentorAvailabilityRule,
  MentorAvailabilityWindow,
  MentorBlackout,
  ProposedSlot,
} from "./types";

const MS_PER_MIN = 60_000;
const MS_PER_DAY = 86_400_000;

const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart < bEnd && bStart < aEnd;

const isBlackedOut = (
  start: Date,
  end: Date,
  blackouts: MentorBlackout[],
) =>
  blackouts.some((b) =>
    overlaps(start, end, new Date(b.starts_at), new Date(b.ends_at)),
  );

const sliceIntoSlots = (
  start: Date,
  end: Date,
  slotMinutes: number,
  source: ProposedSlot["source"],
  sourceId: string,
  label?: string | null,
): ProposedSlot[] => {
  const slots: ProposedSlot[] = [];
  let cursor = start.getTime();
  const last = end.getTime();
  const step = slotMinutes * MS_PER_MIN;
  while (cursor + step <= last) {
    slots.push({
      start: new Date(cursor),
      end: new Date(cursor + step),
      source,
      sourceId,
      label,
    });
    cursor += step;
  }
  return slots;
};

export const expandRules = (
  rules: MentorAvailabilityRule[],
  rangeStart: Date,
  rangeEnd: Date,
): ProposedSlot[] => {
  if (!rules.length) return [];
  const out: ProposedSlot[] = [];
  // Walk day-by-day in UTC. (We intentionally use UTC weekday; mentor's
  // declared timezone is informational for now — Phase 1 simplification.)
  for (
    let day = new Date(
      Date.UTC(
        rangeStart.getUTCFullYear(),
        rangeStart.getUTCMonth(),
        rangeStart.getUTCDate(),
      ),
    );
    day.getTime() <= rangeEnd.getTime();
    day = new Date(day.getTime() + MS_PER_DAY)
  ) {
    const weekday = day.getUTCDay();
    for (const rule of rules) {
      if (!rule.active || rule.weekday !== weekday) continue;
      const slotStart = new Date(day.getTime() + rule.start_minute * MS_PER_MIN);
      const slotEnd = new Date(day.getTime() + rule.end_minute * MS_PER_MIN);
      if (slotEnd <= rangeStart || slotStart >= rangeEnd) continue;
      out.push(
        ...sliceIntoSlots(
          slotStart,
          slotEnd,
          rule.slot_minutes,
          "rule",
          rule.id,
        ),
      );
    }
  }
  return out;
};

export const expandWindows = (
  windows: MentorAvailabilityWindow[],
  rangeStart: Date,
  rangeEnd: Date,
): ProposedSlot[] => {
  const out: ProposedSlot[] = [];
  for (const w of windows) {
    if (!w.active) continue;
    const start = new Date(w.starts_at);
    const end = new Date(w.ends_at);
    if (end <= rangeStart || start >= rangeEnd) continue;
    out.push(
      ...sliceIntoSlots(
        start,
        end,
        w.slot_minutes,
        "window",
        w.id,
        w.label ?? undefined,
      ),
    );
  }
  return out;
};

export interface BuildSlotsArgs {
  rules: MentorAvailabilityRule[];
  windows: MentorAvailabilityWindow[];
  blackouts: MentorBlackout[];
  /** Already-pending or confirmed bookings — those slots are removed. */
  bookedRanges?: Array<{ start: Date; end: Date }>;
  rangeStart: Date;
  rangeEnd: Date;
}

export const buildBookableSlots = ({
  rules,
  windows,
  blackouts,
  bookedRanges = [],
  rangeStart,
  rangeEnd,
}: BuildSlotsArgs): ProposedSlot[] => {
  const all = [
    ...expandRules(rules, rangeStart, rangeEnd),
    ...expandWindows(windows, rangeStart, rangeEnd),
  ]
    .filter((s) => s.start >= rangeStart && s.end <= rangeEnd)
    .filter((s) => !isBlackedOut(s.start, s.end, blackouts))
    .filter(
      (s) => !bookedRanges.some((b) => overlaps(s.start, s.end, b.start, b.end)),
    );

  // De-dup overlapping slots by start time (rule + window can collide).
  const seen = new Set<number>();
  const unique: ProposedSlot[] = [];
  for (const s of all.sort((a, b) => a.start.getTime() - b.start.getTime())) {
    const key = s.start.getTime();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(s);
  }
  return unique;
};

export const groupSlotsByDay = (slots: ProposedSlot[]) => {
  const map = new Map<string, ProposedSlot[]>();
  for (const s of slots) {
    const key = s.start.toISOString().slice(0, 10);
    const arr = map.get(key) ?? [];
    arr.push(s);
    map.set(key, arr);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
};
